/**
 * SearchPanel — universal database search across all entities.
 * Results grouped by category with clickable links to detail pages.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { universalSearch } from '../../api/search';
import { LoadingSpinner, StatusBadge } from '../common';

const CATEGORY_CONFIG = {
  jobs: {
    label: 'Jobs',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    ),
    color: 'text-blue-600 bg-blue-50',
  },
  unit_serial_numbers: {
    label: 'Unit Serial Numbers',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    ),
    color: 'text-indigo-600 bg-indigo-50',
  },
  component_serial_numbers: {
    label: 'Component Serial Numbers',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    ),
    color: 'text-amber-600 bg-amber-50',
  },
  parts: {
    label: 'Parts',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    ),
    color: 'text-green-600 bg-green-50',
  },
  customers: {
    label: 'Customers',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    ),
    color: 'text-purple-600 bg-purple-50',
  },
  vendors: {
    label: 'Vendors',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    ),
    color: 'text-teal-600 bg-teal-50',
  },
  documents: {
    label: 'Documents',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
    color: 'text-red-600 bg-red-50',
  },
  parent_items: {
    label: 'Parent Items',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    ),
    color: 'text-orange-600 bg-orange-50',
  },
};

// Display order
const CATEGORY_ORDER = [
  'jobs', 'unit_serial_numbers', 'component_serial_numbers',
  'parts', 'parent_items', 'customers', 'vendors', 'documents',
];

export default function SearchPanel({ onNavigateToEntity }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  async function handleSearch(e) {
    e?.preventDefault();
    if (!query.trim() || query.trim().length < 2) return;
    setLoading(true);
    setSearched(true);
    try {
      const resp = await universalSearch(query.trim());
      setResults(resp.data || {});
      setTotal(resp.total || 0);
    } catch {
      setResults({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function handleResultClick(item) {
    // If it's a manage entity, switch to that tab
    if (item.manage_entity && onNavigateToEntity) {
      onNavigateToEntity(item.manage_entity);
      return;
    }
    // Otherwise navigate to the link
    if (item.link) {
      navigate(item.link);
    }
  }

  return (
    <div className="p-4 max-w-4xl">
      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search jobs, serial numbers, parts, customers, vendors, documents..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="px-6 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          Search by job number, serial number, part number, customer name, PO#, work order#, or any keyword.
        </p>
      </form>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-10"><LoadingSpinner /></div>
      ) : results && searched ? (
        total > 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">{total} result{total !== 1 ? 's' : ''} found</p>

            {CATEGORY_ORDER.map((catKey) => {
              const items = results[catKey];
              if (!items || items.length === 0) return null;
              const config = CATEGORY_CONFIG[catKey];

              return (
                <div key={catKey} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                    <div className={`w-6 h-6 rounded flex items-center justify-center ${config.color}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {config.icon}
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{config.label}</span>
                    <span className="text-xs text-gray-400">({items.length})</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleResultClick(item)}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-xs text-gray-500 truncate">{item.subtitle}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.status && <StatusBadge status={item.status} />}
                          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-sm text-gray-500">No results found for "{query}"</p>
            <p className="text-xs text-gray-400 mt-1">Try a different search term or check your spelling.</p>
          </div>
        )
      ) : !searched ? (
        <div className="text-center py-16">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-sm text-gray-400">Search across all jobs, serial numbers, parts, customers, and documents.</p>
        </div>
      ) : null}
    </div>
  );
}
